import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { Toaster } from './components/ui/sonner';
import AuthPage from './pages/AuthPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import FeedPage from './pages/FeedPage';
import MorningScanPage from './pages/MorningScanPage';
import CustomScanPage from './pages/CustomScanPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectCreatePage from './pages/ProjectCreatePage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import SettingsPage from './pages/SettingsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import ThemeSettingsPage from './pages/ThemeSettingsPage';
import SyncSchedulesPage from './pages/SyncSchedulesPage';
import SyncLogsPage from './pages/SyncLogsPage';
import OnboardingPage from './pages/OnboardingPage';
import SlackDebugPage from './pages/SlackDebugPage';
import AlertsPage from './pages/AlertsPage';
import AlertSettingsPage from './pages/AlertSettingsPage';
import NotesPage from './pages/NotesPage';
import TeamSettingsPage from './pages/TeamSettingsPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import DataMaintenancePage from './pages/DataMaintenancePage';
import ConversationsPage from './pages/ConversationsPage';
import CreateSignalPage from './pages/CreateSignalPage';
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import InsightsDetailView from './components/home/InsightsDetailView';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  
  return user ? children : <Navigate to="/auth" replace />;
};

function AppRoutes() {
  const { user, onboarded } = useAuth();
  
  return (
    <Routes>
      <Route path="/auth" element={user ? <Navigate to="/home" replace /> : <AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/" element={user ? <Navigate to="/home" replace /> : <LandingPage />} />

      <Route path="/home" element={
        <ProtectedRoute>
          {!onboarded ? <Navigate to="/onboarding" replace /> : <HomePage />}
        </ProtectedRoute>
      } />
      
      <Route path="/onboarding" element={
        <ProtectedRoute>
          {onboarded ? <Navigate to="/feed" replace /> : <OnboardingPage />}
        </ProtectedRoute>
      } />
      
      <Route path="/feed" element={
        <ProtectedRoute>
          {!onboarded ? <Navigate to="/onboarding" replace /> : <FeedPage />}
        </ProtectedRoute>
      } />
      
      <Route path="/morning-scan" element={
        <ProtectedRoute>
          {!onboarded ? <Navigate to="/onboarding" replace /> : <MorningScanPage />}
        </ProtectedRoute>
      } />
      
      <Route path="/custom-scan" element={
        <ProtectedRoute>
          {!onboarded ? <Navigate to="/onboarding" replace /> : <CustomScanPage />}
        </ProtectedRoute>
      } />
      
      <Route path="/projects" element={
        <ProtectedRoute>
          <ProjectsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/projects/new" element={
        <ProtectedRoute>
          <ProjectCreatePage />
        </ProtectedRoute>
      } />
      
      <Route path="/projects/:projectId/briefings/:briefingKey" element={
        <ProtectedRoute>
          <InsightsDetailView />
        </ProtectedRoute>
      } />

      <Route path="/projects/:id/*" element={
        <ProtectedRoute>
          <ProjectDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/integrations" element={
        <ProtectedRoute>
          <IntegrationsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/theme" element={
        <ProtectedRoute>
          <ThemeSettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/sync-schedules" element={
        <ProtectedRoute>
          <SyncSchedulesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/sync-logs" element={
        <ProtectedRoute>
          <SyncLogsPage />
        </ProtectedRoute>
      } />

      <Route path="/settings/alert-thresholds" element={
        <ProtectedRoute>
          <AlertSettingsPage />
        </ProtectedRoute>
      } />

      <Route path="/debug/slack" element={
        <ProtectedRoute>
          <SlackDebugPage />
        </ProtectedRoute>
      } />

      <Route path="/alerts" element={
        <ProtectedRoute>
          <AlertsPage />
        </ProtectedRoute>
      } />

      <Route path="/notes" element={
        <ProtectedRoute>
          {!onboarded ? <Navigate to="/onboarding" replace /> : <NotesPage />}
        </ProtectedRoute>
      } />

      <Route path="/settings/team" element={
        <ProtectedRoute>
          <TeamSettingsPage />
        </ProtectedRoute>
      } />

      <Route path="/settings/data-maintenance" element={
        <ProtectedRoute>
          <DataMaintenancePage />
        </ProtectedRoute>
      } />

      <Route path="/conversations" element={
        <ProtectedRoute>
          {!onboarded ? <Navigate to="/onboarding" replace /> : <ConversationsPage />}
        </ProtectedRoute>
      } />

      <Route path="/conversations/:id" element={
        <ProtectedRoute>
          <Navigate to="/conversations" replace />
        </ProtectedRoute>
      } />

      <Route path="/signals/create" element={
        <ProtectedRoute>
          <CreateSignalPage />
        </ProtectedRoute>
      } />

      {/* Invite acceptance page - accessible without auth to show invite details */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ChatProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </ChatProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;